import React, { useEffect } from 'react';

const ProductConversionsSection = ({
  conversions = [],
  units = [],
  destinationUnitId = null,
  destinationUnit = null,
  onChange,
  readOnly = false,
}) => {
  const rows = conversions.length > 0 ? conversions : [];
  const destId = destinationUnitId ? Number(destinationUnitId) : null;

  useEffect(() => {
    if (!destId || !onChange || rows.length === 0) return;

    const needsSync = rows.some(
      (row) =>
        Number(row.to_unit_id) !== destId || Number(row.from_unit_id) === destId
    );
    if (!needsSync) return;

    onChange(
      rows.map((row) => ({
        ...row,
        to_unit_id: destId,
        from_unit_id: Number(row.from_unit_id) === destId ? '' : row.from_unit_id,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destId]);

  const updateRows = (nextRows) => {
    if (onChange) onChange(nextRows);
  };

  const handleRowChange = (index, field, value) => {
    const next = rows.map((row, i) => (i === index ? { ...row, [field]: value } : row));
    updateRows(next);
  };

  const handleAddRow = () => {
    if (!destId) return;
    updateRows([
      ...rows,
      {
        key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        from_unit_id: '',
        to_unit_id: destId,
        conversion_rate: '',
        description: '',
        is_active: true,
      },
    ]);
  };

  const handleRemoveRow = (index) => {
    updateRows(rows.filter((_, i) => i !== index));
  };

  const availableFromUnits = () =>
    units.filter((unit) => !destId || unit.id !== destId);

  const destinationLabel = destinationUnit
    ? `${destinationUnit.symbol} — ${destinationUnit.name}`
    : '—';

  const fieldClass =
    'w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all';

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4 mb-4 pb-3 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
            Tasas de conversión
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Unidad destino: <span className="font-medium text-gray-700">{destinationLabel}</span>.
            Indique cuántas unidades destino equivalen a 1 unidad origen.
          </p>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={handleAddRow}
            disabled={!destId}
            className="flex-shrink-0 text-sm font-medium text-teal-600 hover:text-teal-700 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            + Agregar
          </button>
        )}
      </div>

      {!destId ? (
        <p className="text-sm text-gray-500">
          Seleccione la unidad de medida del producto para configurar conversiones.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">
          Sin conversiones configuradas.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((row, index) => {
            const fromUnit = units.find((u) => u.id === Number(row.from_unit_id));
            const toUnit = destinationUnit || units.find((u) => u.id === destId);
            const rate = parseFloat(row.conversion_rate);

            return (
              <div
                key={row.key || row.id || index}
                className="border border-gray-100 rounded-lg p-4 bg-gray-50/50 w-full"
              >
                <div className="flex flex-col lg:flex-row lg:items-end gap-4 w-full">
                  <div className="flex-1 min-w-0 w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unidad origen</label>
                    <select
                      value={row.from_unit_id || ''}
                      onChange={(e) => handleRowChange(index, 'from_unit_id', e.target.value)}
                      disabled={readOnly}
                      className={`${fieldClass} w-full`}
                    >
                      <option value="">Seleccione</option>
                      {availableFromUnits().map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.symbol} — {unit.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex-1 min-w-0 w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unidad destino</label>
                    <div className={`${fieldClass} w-full bg-gray-100 text-gray-600 cursor-default`}>
                      {destinationLabel}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0 w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tasa</label>
                    <input
                      type="number"
                      value={row.conversion_rate}
                      onChange={(e) => handleRowChange(index, 'conversion_rate', e.target.value)}
                      disabled={readOnly}
                      min="0.0001"
                      step="0.0001"
                      placeholder="0"
                      className={`${fieldClass} w-full`}
                    />
                  </div>

                  <div className="flex shrink-0 items-center gap-4 lg:pb-2.5">
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={row.is_active !== false}
                        onChange={(e) => handleRowChange(index, 'is_active', e.target.checked)}
                        disabled={readOnly}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      Activa
                    </label>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(index)}
                        className="text-sm text-gray-500 hover:text-red-600 transition-colors whitespace-nowrap"
                      >
                        Quitar
                      </button>
                    )}
                  </div>
                </div>

                {fromUnit && toUnit && !Number.isNaN(rate) && rate > 0 && (
                  <p className="mt-3 text-xs text-gray-500">
                    1 {fromUnit.symbol} = {rate} {toUnit.symbol}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductConversionsSection;
